import { CoinbaseWalletProvider, CoinbaseWalletSDK } from "@coinbase/wallet-sdk";
import { Web3Config } from "../config/Web3Config";
import { ILogger } from "../../cityversetycoon/logger/ILogger";
import { ChainUtils } from "../utils/ChainUtils";
import { Event } from "../../shared/event-system/Event";
import { PromiseCompletionSource } from "../../shared/async/PromiseCompletionSource";
import { VisibilityTimeoutObserver } from "../../tools/VisibilityTimeoutObserver";
import { EthereumProvider } from "./rpc/EthereumProvider";
import { RPCError } from "./rpc/RPCError";
import { RPCErrorCode } from "./rpc/RPCErrorCode";
import { RPCProvider } from "./rpc/RPCProvider";
import { IExternalWallet } from "./IExternalWallet";
import { ExternalWalletType } from "./ExternalWalletType";

export class CoinBase implements IExternalWallet {
    public readonly onDataChanged: Event<void>;

    private readonly web3Config: Web3Config;
    private readonly logger: ILogger;
    private readonly coinbaseWalletSdk: CoinbaseWalletSDK;
    private sdkProvider: CoinbaseWalletProvider;
    private rpcProvider: RPCProvider;
    private readonly timeoutObserver: VisibilityTimeoutObserver;

    private connectPCS: PromiseCompletionSource<Array<string>>;
    private addChainPCS: PromiseCompletionSource<void>;
    private switchNetworkPCS: PromiseCompletionSource<void>;
    private signMessagePCS: PromiseCompletionSource<string>;
    private burnNFTPCS: PromiseCompletionSource<string>;

    private addressesValue: Array<string>;
    private chainIdValue: string;

    public get type(): ExternalWalletType {
        return ExternalWalletType.COINBASE;
    }

    public get connected(): boolean {
        return this.sdkProvider != null && this.addresses?.length > 0;
    }

    public get addresses(): Array<string> {
        return this.addressesValue;
    }

    public get chainId(): string {
        return this.chainIdValue;
    }

    public get supportedChainSelected(): boolean {
        return this.chainIdValue === ChainUtils.idToHex(this.web3Config.targetChain.id);
    }

    public get provider(): EthereumProvider {
        return this.sdkProvider;
    }

    public constructor(web3Config: Web3Config, logger: ILogger, timeoutObserver: VisibilityTimeoutObserver) {
        this.web3Config = web3Config;
        this.logger = logger;
        this.timeoutObserver = timeoutObserver;
        this.onDataChanged = new Event();
        this.coinbaseWalletSdk = new CoinbaseWalletSDK({
            appName: this.web3Config.appName,
            enableMobileWalletLink: true
        });
        this.initializeProvider();
    }

    public async autoConnect(): Promise<boolean> {
        this.logger.debug(`CoinBase auto connect`);
        try {
            this.initializeProvider();
            await this.updateAddress();
            if (!this.connected) {
                return false;
            }
            if (this.chainIdValue == null) {
                await this.updateChainId();
            }
            return this.connected;
        }
        catch (e: unknown) {
            this.logger.debug(`Exception on auto connect CoinBase: ${e}`);
            return false;
        }
    }

    public async connect(): Promise<boolean> {
        this.logger.debug("CoinBase connect");
        if (this.connected) {
            this.logger.debug(`CoinBase already connected`);
            return true;
        }
        try {
            this.initializeProvider();
            this.connectPCS = new PromiseCompletionSource();
            this.timeoutObserver.addOperation(this.connectPCS, () => !this.connected);
            const accounts = await this.requestWithPCS(this.rpcProvider.requestAccounts(), () => this.connectPCS);
            this.timeoutObserver.removeOperation(this.connectPCS);
            this.connectPCS = null;
            this.addressesValue = accounts;
            if (!this.connected) {
                return false;
            }
            if (this.chainIdValue == null) {
                await this.updateChainId();
            }
            return this.connected;
        }
        catch (error: unknown) {
            this.timeoutObserver.removeOperation(this.connectPCS);
            this.connectPCS = null;
            if (error instanceof RPCError && error.code === RPCErrorCode.REJECTED) {
                this.logger.debug("User reject connection");
                return false;
            }
            this.logger.error(`Exception on connect CoinBase: ${error}`);
            return false;
        }
    }

    public async switchNetwork(): Promise<boolean> {
        this.logger.debug("CoinBase switch network");
        try {
            this.initializeProvider();
            if (!this.connected) {
                this.logger.debug(`Coinbase unable to switch network, not connected`);
                return false;
            }

            if (this.supportedChainSelected) {
                this.logger.debug(`Supported chain already selected`);
                return true;
            }

            this.addChainPCS = new PromiseCompletionSource();
            this.timeoutObserver.addOperation(this.addChainPCS, () => !this.supportedChainSelected);
            await this.requestWithPCS(this.rpcProvider.addChain(this.web3Config.targetChain), () => this.addChainPCS);
            this.timeoutObserver.removeOperation(this.addChainPCS);
            this.addChainPCS = null;

            this.switchNetworkPCS = new PromiseCompletionSource();
            this.timeoutObserver.addOperation(this.switchNetworkPCS, () => !this.supportedChainSelected);
            await this.requestWithPCS(this.rpcProvider.switchChain(this.web3Config.targetChain.id), () => this.switchNetworkPCS);
            this.timeoutObserver.removeOperation(this.switchNetworkPCS);
            this.switchNetworkPCS = null;

            return this.supportedChainSelected;
        }
        catch (error: unknown) {
            this.timeoutObserver.removeOperation(this.addChainPCS);
            this.addChainPCS = null;
            this.timeoutObserver.removeOperation(this.switchNetworkPCS);
            this.switchNetworkPCS = null;
            if (error instanceof RPCError && error.code === RPCErrorCode.REJECTED) {
                this.logger.debug("User reject switch chain");
                return false;
            }
            this.logger.error("Exception on sitch chain: ", error);
            return false;
        }
    }

    public async signMessage(address: string, nonce: string): Promise<string> {
        this.logger.debug(`CoinBase sign message, nonce: ${nonce}`);
        const addressIndex = this.addresses.findIndex((connectedAddress) => connectedAddress.toLowerCase() == address.toLowerCase())
        if (addressIndex === -1) {
            this.logger.error(`Attemp of sign message using not compatible address (${address}), list of connected addresses: ${this.addresses}`);
            return null;
        }
        address = this.addresses[addressIndex];
        try {
            this.initializeProvider();
            if (!this.connected) {
                this.logger.debug(`Coinbase unable to sign message, not connected`);
                return null;
            }
            this.signMessagePCS = new PromiseCompletionSource();
            this.timeoutObserver.addOperation(this.signMessagePCS);
            const result = await this.requestWithPCS(this.rpcProvider.signMessage(nonce, address), () => this.signMessagePCS);
            this.timeoutObserver.removeOperation(this.signMessagePCS);
            this.signMessagePCS = null;
            return result;
        }
        catch (error: unknown) {
            this.timeoutObserver.removeOperation(this.signMessagePCS);
            this.signMessagePCS = null;
            if (error instanceof RPCError && error.code === RPCErrorCode.REJECTED) {
                this.logger.debug("User reject sign message");
                return null;
            }
            this.logger.error("Exception on signing message: ", error);
            return null;
        }
    }

    public async burnNFT(address: string, burnData: string): Promise<string> {
        this.logger.debug(`CoinBase burn nft`);
        if (!this.addresses.includes(address)) {
            this.logger.error(`Attemp of sign message using not compatible address (${address}), list of connected addresses: ${this.addresses}`);
            return null;
        }
        try {
            this.initializeProvider();
            if (!this.connected) {
                this.logger.debug(`Attemp of burn nft from not connected wallet`);
                return null;
            }
            this.burnNFTPCS = new PromiseCompletionSource();
            this.timeoutObserver.addOperation(this.burnNFTPCS);
            const result = await this.requestWithPCS(
                this.rpcProvider.sendTransaction(
                    this.web3Config.packagesCollectionAddress,
                    address,
                    burnData),
                () => this.burnNFTPCS
            );
            this.timeoutObserver.removeOperation(this.burnNFTPCS);
            this.burnNFTPCS = null;

            return result;
        }
        catch (error: unknown) {
            this.timeoutObserver.removeOperation(this.burnNFTPCS);
            this.burnNFTPCS = null;
            if (error instanceof RPCError && error.code === RPCErrorCode.REJECTED) {
                this.logger.debug("User reject burn");
                return null;
            }
            this.logger.error("Exception on burn nft: ", error);
            return null;
        }
    }

    public async disconnect(): Promise<void> {
        this.logger.debug("CoinBase disconnect");
        try {
            this.sdkProvider.disconnect();
        }
        catch (e: unknown) {
            this.logger.debug(`Exception on disconnect: ${e}`);
        }
        finally {
            this.logger.debug("CoinBase clear provider");
            this.connectPCS?.error("User disconnected");
            this.addChainPCS?.error("User disconnected");
            this.switchNetworkPCS?.error("User disconnected");
            this.signMessagePCS?.error("User disconnected");
            this.burnNFTPCS?.error("User disconnected");
            this.sdkProvider.removeAllListeners();
            this.sdkProvider = null;
            this.rpcProvider = null;
            this.addressesValue = null;
            this.chainIdValue = null;
            this.onDataChanged.emit();
        }
    }

    private async updateAddress(): Promise<void> {
        this.logger.debug("CoinBase update accounts");
        try {
            const accounts: Array<string> = await this.rpcProvider.getAccounts();
            this.logger.debug(`getAccounts result: ${accounts}`);
            this.addressesValue = accounts?.length > 0 ? accounts : this.addressesValue;
        }
        catch (e: unknown) {
            this.logger.debug(`[CoinBase] Exception on updating address: ${JSON.stringify(e)}`);
        }
    }

    private async updateChainId(): Promise<void> {
        this.logger.debug("CoinBase update chain id");
        try {
            const chainId: string = await this.rpcProvider.getChainId();
            this.logger.debug(`eth_chainId request result: ${chainId}`);
            this.chainIdValue = chainId;
        }
        catch (e: unknown) {
            this.logger.debug(`[CoinBase] Exception on updating chainId: ${e}`);
        }
    }

    private onConnect(connectInfo: any): void {
        console.log("onConnect: ", connectInfo);
    }

    private onDisconnect(error: any): void {
        console.log("onDisconnect: ", error);
    }

    private onAccountsChanged(accounts: Array<string>): void {
        this.logger.debug(`CoinBase account changed: ${accounts}`);
        this.addressesValue = accounts;
        if (this.connected && (this.addressesValue == null || this.addressesValue.length === 0)) {
            this.logger.debug(`Account changed, no address, disconnect`);
            this.disconnect();
        }
        else {
            this.connectPCS?.complete(accounts);
        }
    }

    private onChainChanged(chainId: string): void {
        console.log("onChainChanged: ", chainId);
        this.chainIdValue = typeof chainId === "string" ? chainId : ChainUtils.idToHex(chainId);
        if (this.supportedChainSelected) {
            this.addChainPCS?.complete();
            this.switchNetworkPCS?.complete();
        }
        this.onDataChanged.emit();
    }

    private onMessage(message: any): void {
        console.log("onMessage: ", message);
    }

    private initializeProvider(): void {
        if (this.sdkProvider != null) {
            return;
        }
        this.sdkProvider = this.coinbaseWalletSdk.makeWeb3Provider();
        this.rpcProvider = new RPCProvider(this.sdkProvider);
        this.sdkProvider.addListener('connect', this.onConnect, this);
        this.sdkProvider.addListener('disconnect', this.onDisconnect, this);
        this.sdkProvider.addListener('accountsChanged', this.onAccountsChanged, this);
        this.sdkProvider.addListener('chainChanged', this.onChainChanged, this);
        this.sdkProvider.addListener('message', this.onMessage, this);
    }

    private async requestWithPCS<T>(operation: Promise<T>, pcsGetter: () => PromiseCompletionSource<T>): Promise<T> {
        operation.then((result: T) => {
            this.logger.debug(`Operation completed with result: ${result}`);
            const pcs = pcsGetter();
            pcs?.complete(result);
        }, (error: unknown) => {
            this.logger.debug(`Operation failed reason: ${JSON.stringify(error)}`);
            const pcs = pcsGetter();
            pcs?.error(error);
        });
        return await pcsGetter()?.result;
    }
}